import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  MapPin, Phone, Clock, Shield, CheckCircle, Star, Award, 
  Wifi, Smartphone, Camera, Bell, Lock, Zap, Users, ArrowRight,
  Home, Building2, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { AnimatedSection } from '../hooks/useScrollAnimation';
import { BACKEND_URL } from '../config/api';

const API = `${BACKEND_URL}/api`;

const AlarmeMontpellierPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    propertyType: 'maison',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          subject: `[SEO] Demande devis alarme Montpellier - ${formData.propertyType}`
        })
      });

      if (response.ok) {
        setSubmitted(true);
        setFormData({ name: '', email: '', phone: '', address: '', propertyType: 'maison', message: '' });
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // Zones d'intervention
  const zones = [
    { name: 'Montpellier Centre', distance: '0 km', available: true },
    { name: 'Antigone', distance: '2 km', available: true },
    { name: 'Port Marianne', distance: '3 km', available: true },
    { name: 'Castelnau-le-Lez', distance: '5 km', available: true },
    { name: 'Lattes', distance: '6 km', available: true },
    { name: 'Pérols', distance: '8 km', available: true },
    { name: 'Mauguio', distance: '12 km', available: true },
    { name: 'Saint-Jean-de-Védas', distance: '7 km', available: true },
    { name: 'Grabels', distance: '8 km', available: true },
    { name: 'Juvignac', distance: '9 km', available: true },
    { name: 'Palavas-les-Flots', distance: '12 km', available: true },
    { name: 'Villeneuve-lès-Maguelone', distance: '14 km', available: true },
  ];

  // Avantages Ajax Systems
  const ajaxAdvantages = [
    {
      icon: Wifi,
      title: 'Technologie sans fil Jeweller',
      description: 'Communication radio cryptée jusqu\'à 2000m, immunisée contre les interférences'
    },
    {
      icon: Smartphone,
      title: 'Application Ajax gratuite',
      description: 'Contrôlez votre alarme depuis votre smartphone, notifications instantanées'
    },
    {
      icon: Shield,
      title: 'Détection anti-sabotage',
      description: 'Protection contre le brouillage radio et les tentatives de neutralisation'
    },
    {
      icon: Zap,
      title: 'Autonomie exceptionnelle',
      description: 'Jusqu\'à 7 ans d\'autonomie sur piles, alertes batterie faible'
    },
    {
      icon: Camera,
      title: 'Vérification photo',
      description: 'MotionCam envoie des photos en cas d\'alarme pour lever le doute'
    },
    {
      icon: Lock,
      title: 'Certifié Grade 2',
      description: 'Conforme aux normes européennes EN 50131, assurance reconnue'
    },
  ];

  // Témoignages clients
  const testimonials = [
    {
      name: 'Laurent D.',
      location: 'Montpellier - Antigone',
      rating: 5,
      text: 'Installation rapide et professionnelle. Le système Ajax est vraiment impressionnant, l\'application est très intuitive. Je recommande vivement !'
    },
    {
      name: 'Sophie M.',
      location: 'Castelnau-le-Lez',
      rating: 5,
      text: 'Après un cambriolage, j\'ai fait installer un système complet. L\'équipe a été à l\'écoute et le devis très compétitif. Tranquillité retrouvée !'
    },
    {
      name: 'Marc P.',
      location: 'Lattes',
      rating: 5,
      text: 'Excellente prestation pour ma maison. Les détecteurs MotionCam sont un vrai plus pour la levée de doute. Service après-vente réactif.'
    },
  ];

  // FAQ SEO
  const faqItems = [
    {
      question: 'Quel est le prix d\'une alarme à Montpellier ?',
      answer: 'Le prix d\'un système d\'alarme Ajax Systems à Montpellier varie de 800€ à 2500€ selon la configuration. Un kit de base (centrale + 3 détecteurs + sirène) commence à 899€ TTC installation comprise. Nous proposons des devis gratuits et personnalisés.'
    },
    {
      question: 'Combien de temps dure l\'installation d\'une alarme ?',
      answer: 'L\'installation d\'un système Ajax sans fil prend généralement entre 2 et 4 heures selon la taille du logement. Nos techniciens certifiés interviennent à Montpellier et dans toute l\'agglomération sous 48-72h.'
    },
    {
      question: 'L\'alarme Ajax fonctionne-t-elle sans internet ?',
      answer: 'Oui ! La centrale Hub 2 (4G) dispose d\'une carte SIM intégrée qui prend le relais en cas de coupure internet. Vous êtes alerté sur votre smartphone même si votre box est déconnectée.'
    },
    {
      question: 'Quelle est la zone d\'intervention autour de Montpellier ?',
      answer: 'Nous intervenons dans un rayon de 50 km autour de Montpellier : Castelnau-le-Lez, Lattes, Mauguio, Palavas, Saint-Jean-de-Védas, Grabels, Pérols, et toutes les communes de l\'Hérault.'
    },
    {
      question: 'Ajax Systems est-il compatible avec ma assurance ?',
      answer: 'Oui, les systèmes Ajax sont certifiés Grade 2 selon la norme EN 50131, reconnue par toutes les compagnies d\'assurance. Vous pouvez bénéficier d\'une réduction sur votre prime habitation.'
    },
    {
      question: 'Proposez-vous la télésurveillance ?',
      answer: 'Oui, nous proposons un forfait télésurveillance 24/7 optionnel avec intervention sur alarme. Vous pouvez aussi utiliser le système en autonomie avec alertes directes sur votre smartphone.'
    },
  ];

  return (
    <>
      <Helmet>
        <title>Alarme Montpellier : Installation Ajax Systems | Devis Gratuit 24h | FGS Alarme Montpellier</title>
        <meta name="description" content="★ Installateur alarme Montpellier certifié Ajax Systems. Alarme maison sans fil, vidéosurveillance IA, télésurveillance 24/7. Intervention Hérault (34) sous 48h. Devis gratuit. ☎ 07 45 04 09 29" />
        <meta name="keywords" content="alarme Montpellier, installation alarme Montpellier, alarme maison Montpellier, installateur alarme 34, Ajax Systems Montpellier, vidéosurveillance Montpellier, alarme sans fil Montpellier, alarme Hérault, système alarme Montpellier, alarme anti-intrusion Montpellier, devis alarme Montpellier, prix alarme Montpellier, alarme connectée Montpellier, télésurveillance Montpellier, caméra surveillance Montpellier" />
        <link rel="canonical" href="https://maprotection-ia.fr/alarme-montpellier" />
        
        {/* Geo Meta Tags */}
        <meta name="geo.region" content="FR-34" />
        <meta name="geo.placename" content="Montpellier" />
        <meta name="geo.position" content="43.6108;3.8767" />
        <meta name="ICBM" content="43.6108, 3.8767" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Alarme Montpellier : Installation Ajax Systems | Devis Gratuit" />
        <meta property="og:description" content="Installateur certifié Ajax Systems à Montpellier. Alarme sans fil, vidéosurveillance IA, garantie 4 ans. Devis gratuit sous 24h." />
        <meta property="og:url" content="https://maprotection-ia.fr/alarme-montpellier" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:image" content="https://maprotection-ia.fr/og-image-fb.png" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Alarme Montpellier | Installation Ajax Systems" />
        <meta name="twitter:description" content="Installateur certifié Ajax Systems. Devis gratuit sous 24h." />
        
        {/* Schema.org LocalBusiness - Amélioré */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "@id": "https://maprotection-ia.fr/#organization",
            "name": "FGS Alarme Montpellier",
            "alternateName": "FGS Alarme",
            "description": "Installateur certifié Ajax Systems à Montpellier. Installation alarme sans fil, vidéosurveillance IA, télésurveillance 24/7 dans l'Hérault (34).",
            "url": "https://maprotection-ia.fr/alarme-montpellier",
            "telephone": "+33745040929",
            "email": "contact@maprotection-ia.fr",
            "image": "https://maprotection-ia.fr/og-image-fb.png",
            "logo": "https://maprotection-ia.fr/favicon-512x512.png",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "6 Bis Boulevard Berthelot, Bureau 3",
              "addressLocality": "Montpellier",
              "addressRegion": "Occitanie",
              "postalCode": "34000",
              "addressCountry": "FR"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": 43.6108,
              "longitude": 3.8767
            },
            "areaServed": [
              {"@type": "City", "name": "Montpellier"},
              {"@type": "City", "name": "Castelnau-le-Lez"},
              {"@type": "City", "name": "Lattes"},
              {"@type": "City", "name": "Mauguio"},
              {"@type": "City", "name": "Pérols"},
              {"@type": "City", "name": "Saint-Jean-de-Védas"},
              {"@type": "City", "name": "Grabels"},
              {"@type": "City", "name": "Palavas-les-Flots"},
              {"@type": "City", "name": "Juvignac"},
              {"@type": "City", "name": "Villeneuve-lès-Maguelone"},
              {"@type": "AdministrativeArea", "name": "Hérault"}
            ],
            "priceRange": "€€",
            "currenciesAccepted": "EUR",
            "paymentAccepted": "Cash, Credit Card, Bank Transfer",
            "openingHoursSpecification": [
              {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                "opens": "09:00",
                "closes": "18:00"
              }
            ],
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "bestRating": "5",
              "worstRating": "1",
              "reviewCount": "47"
            },
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Services de sécurité",
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Installation alarme Montpellier",
                    "description": "Installation professionnelle de systèmes d'alarme Ajax Systems"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Vidéosurveillance Montpellier",
                    "description": "Installation de caméras de surveillance avec détection IA"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Télésurveillance 24/7",
                    "description": "Service de télésurveillance avec intervention sur alarme"
                  }
                }
              ]
            },
            "sameAs": [
              "https://www.facebook.com/fgssecurity",
              "https://www.linkedin.com/company/fgssecurity"
            ]
          })}
        </script>

        {/* Schema.org FAQPage */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqItems.map(item => ({
              "@type": "Question",
              "name": item.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": item.answer
              }
            }))
          })}
        </script>

        {/* Schema.org Service */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Installation alarme Montpellier",
            "description": "Service d'installation d'alarmes Ajax Systems à Montpellier et dans l'Hérault. Alarme sans fil professionnelle, certifiée Grade 2.",
            "provider": {
              "@type": "LocalBusiness",
              "name": "FGS Alarme Montpellier"
            },
            "areaServed": {
              "@type": "City",
              "name": "Montpellier"
            },
            "serviceType": "Installation alarme"
          })}
        </script>

        {/* Schema.org BreadcrumbList */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Accueil",
                "item": "https://maprotection-ia.fr"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Alarme Montpellier",
                "item": "https://maprotection-ia.fr/alarme-montpellier"
              }
            ]
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-fgs-bg">
        {/* Hero Section */}
        <section className="relative py-12 lg:py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 via-transparent to-blue-500/5" />
          <div className="absolute top-20 right-10 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <AnimatedSection animation="fade-up" immediate>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full mb-6">
                  <MapPin className="w-4 h-4 text-emerald-400" />
                  <span className="text-emerald-400 text-sm font-medium">Installateur agréé Ajax Systems à Montpellier</span>
                </div>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={100} immediate>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                  Installation <span className="text-emerald-400">Alarme</span> à{' '}
                  <span className="text-emerald-400">Montpellier</span>
                </h1>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={200} immediate>
                <p className="text-xl text-white/70 mb-8 max-w-2xl mx-auto">
                  Protégez votre maison ou entreprise avec les systèmes d'alarme <strong>Ajax Systems</strong>, 
                  leader européen de la sécurité sans fil. Installation professionnelle dans tout l'Hérault.
                </p>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={300} immediate>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a href="#devis" className="inline-block">
                    <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-6 text-lg">
                      <Phone className="w-5 h-5 mr-2" />
                      Devis gratuit sous 24h
                    </Button>
                  </a>
                  <Link to="/shop">
                    <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/5 px-8 py-6 text-lg">
                      Voir nos produits
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                </div>
              </AnimatedSection>

              {/* Trust badges */}
              <AnimatedSection animation="fade-up" delay={400} immediate>
                <div className="flex flex-wrap justify-center gap-6 mt-12">
                  <div className="flex items-center gap-2 text-white/60">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                    <span>Garantie 4 ans</span>
                  </div>
                  <div className="flex items-center gap-2 text-white/60">
                    <Shield className="w-5 h-5 text-emerald-400" />
                    <span>Certifié Grade 2</span>
                  </div>
                  <div className="flex items-center gap-2 text-white/60">
                    <Clock className="w-5 h-5 text-emerald-400" />
                    <span>Intervention 48-72h</span>
                  </div>
                  <div className="flex items-center gap-2 text-white/60">
                    <Star className="w-5 h-5 text-emerald-400" />
                    <span>4.9/5 (47 avis)</span>
                  </div>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </section>

        {/* Pourquoi Ajax Systems */}
        <section className="py-20 bg-black/30">
          <div className="container mx-auto px-4">
            <AnimatedSection animation="fade-up">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Pourquoi choisir <span className="text-emerald-400">Ajax Systems</span> ?
                </h2>
                <p className="text-white/60 max-w-2xl mx-auto">
                  Leader européen des systèmes de sécurité sans fil, Ajax équipe plus de 2 millions de foyers dans le monde.
                </p>
              </div>
            </AnimatedSection>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {ajaxAdvantages.map((advantage, index) => (
                <AnimatedSection key={index} animation="fade-up" delay={index * 100}>
                  <div className="p-6 bg-white/[0.02] border border-white/5 rounded-2xl hover:border-emerald-500/30 transition-all group">
                    <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-emerald-500/20 transition-colors">
                      <advantage.icon className="w-6 h-6 text-emerald-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">{advantage.title}</h3>
                    <p className="text-white/60 text-sm">{advantage.description}</p>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>

        {/* Zones d'intervention */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <AnimatedSection animation="fade-up">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Zones d'intervention autour de <span className="text-emerald-400">Montpellier</span>
                </h2>
                <p className="text-white/60 max-w-2xl mx-auto">
                  Nous intervenons dans toute l'agglomération de Montpellier et l'Hérault pour l'installation de votre alarme.
                </p>
              </div>
            </AnimatedSection>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {zones.map((zone, index) => (
                <AnimatedSection key={index} animation="fade-up" delay={index * 50}>
                  <div className="p-4 bg-white/[0.02] border border-white/5 rounded-xl hover:border-emerald-500/30 transition-all">
                    <div className="flex items-center gap-3">
                      <MapPin className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                      <div>
                        <p className="text-white font-medium">{zone.name}</p>
                        <p className="text-white/40 text-sm">{zone.distance}</p>
                      </div>
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>

            <AnimatedSection animation="fade-up" delay={600}>
              <p className="text-center text-white/50 mt-8">
                Votre ville n'est pas listée ? Contactez-nous, nous intervenons dans un rayon de 50 km autour de Montpellier.
              </p>
            </AnimatedSection>
          </div>
        </section>

        {/* Section SEO - Pourquoi installer une alarme à Montpellier */}
        <section className="py-20 bg-black/30">
          <div className="container mx-auto px-4">
            <AnimatedSection animation="fade-up">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 text-center">
                  Pourquoi installer une <span className="text-emerald-400">alarme à Montpellier</span> ?
                </h2>
                
                <div className="prose prose-lg prose-invert max-w-none">
                  <p className="text-white/70 text-lg leading-relaxed mb-6">
                    <strong className="text-white">Montpellier</strong>, 7ème ville de France avec plus de 300 000 habitants, 
                    connaît malheureusement un taux de cambriolages supérieur à la moyenne nationale. En 2024, 
                    l'<strong className="text-white">Hérault (34)</strong> a enregistré plus de 15 000 cambriolages, 
                    plaçant le département parmi les plus touchés de la région Occitanie.
                  </p>
                  
                  <p className="text-white/70 text-lg leading-relaxed mb-6">
                    Face à cette réalité, l'<strong className="text-white">installation d'une alarme à Montpellier</strong> n'est 
                    plus un luxe mais une nécessité. Les quartiers résidentiels comme <strong className="text-white">Antigone</strong>, 
                    <strong className="text-white"> Port Marianne</strong>, <strong className="text-white">Les Aubes</strong>, 
                    <strong className="text-white"> Aiguelongue</strong> ou <strong className="text-white">Hôpitaux-Facultés</strong> sont 
                    particulièrement ciblés par les cambrioleurs.
                  </p>

                  <p className="text-white/70 text-lg leading-relaxed mb-6">
                    En tant qu'<strong className="text-white">installateur agréé Ajax Systems à Montpellier</strong>, 
                    FGS Alarme Montpellier vous propose des <strong className="text-white">systèmes d'alarme sans fil professionnels</strong>, 
                    certifiés Grade 2 selon la norme européenne EN 50131. Cette certification est reconnue par toutes les 
                    <strong className="text-white"> compagnies d'assurance</strong> et peut vous permettre de bénéficier 
                    d'une réduction sur votre prime habitation.
                  </p>
                </div>

                {/* Stats Montpellier */}
                <div className="grid md:grid-cols-3 gap-6 mt-12">
                  <div className="p-6 bg-white/[0.02] border border-white/10 rounded-xl text-center">
                    <p className="text-4xl font-bold text-emerald-400 mb-2">15 000+</p>
                    <p className="text-white/60 text-sm">Cambriolages dans l'Hérault en 2024</p>
                  </div>
                  <div className="p-6 bg-white/[0.02] border border-white/10 rounded-xl text-center">
                    <p className="text-4xl font-bold text-emerald-400 mb-2">80%</p>
                    <p className="text-white/60 text-sm">Des cambrioleurs fuient face à une alarme</p>
                  </div>
                  <div className="p-6 bg-white/[0.02] border border-white/10 rounded-xl text-center">
                    <p className="text-4xl font-bold text-emerald-400 mb-2">-15%</p>
                    <p className="text-white/60 text-sm">Sur votre assurance habitation</p>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* Section SEO - Nos services à Montpellier */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <AnimatedSection animation="fade-up">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Nos services d'<span className="text-emerald-400">alarme à Montpellier</span>
                </h2>
                <p className="text-white/60 max-w-2xl mx-auto">
                  Installation, maintenance et télésurveillance pour particuliers et professionnels
                </p>
              </div>
            </AnimatedSection>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              <AnimatedSection animation="fade-up" delay={100}>
                <div className="p-6 bg-white/[0.02] border border-white/5 rounded-xl">
                  <Shield className="w-10 h-10 text-emerald-400 mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-2">Alarme maison Montpellier</h3>
                  <p className="text-white/60 text-sm">
                    Protection complète de votre domicile avec détecteurs de mouvement, d'ouverture et sirènes.
                  </p>
                </div>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={200}>
                <div className="p-6 bg-white/[0.02] border border-white/5 rounded-xl">
                  <Camera className="w-10 h-10 text-emerald-400 mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-2">Vidéosurveillance Montpellier</h3>
                  <p className="text-white/60 text-sm">
                    Caméras Ajax avec détection IA, vision nocturne couleur et accès à distance via smartphone.
                  </p>
                </div>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={300}>
                <div className="p-6 bg-white/[0.02] border border-white/5 rounded-xl">
                  <Bell className="w-10 h-10 text-emerald-400 mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-2">Télésurveillance 24/7</h3>
                  <p className="text-white/60 text-sm">
                    Centre de surveillance avec intervention sur alarme et levée de doute par photo/vidéo.
                  </p>
                </div>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={400}>
                <div className="p-6 bg-white/[0.02] border border-white/5 rounded-xl">
                  <Users className="w-10 h-10 text-emerald-400 mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-2">Alarme entreprise Montpellier</h3>
                  <p className="text-white/60 text-sm">
                    Solutions professionnelles Grade 2 et Grade 3 pour commerces, bureaux et sites sensibles.
                  </p>
                </div>
              </AnimatedSection>
            </div>

            {/* CTA vers boutique */}
            <AnimatedSection animation="fade-up" delay={500}>
              <div className="text-center mt-12">
                <Link to="/shop">
                  <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-white">
                    Voir notre catalogue Ajax Systems
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* Témoignages */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <AnimatedSection animation="fade-up">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Avis de nos clients à <span className="text-emerald-400">Montpellier</span>
                </h2>
                <div className="flex items-center justify-center gap-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-6 h-6 text-yellow-400 fill-yellow-400" />
                  ))}
                  <span className="text-white/60 ml-2">4.9/5 basé sur 47 avis</span>
                </div>
              </div>
            </AnimatedSection>

            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {testimonials.map((testimonial, index) => (
                <AnimatedSection key={index} animation="fade-up" delay={index * 100}>
                  <div className="p-6 bg-white/[0.02] border border-white/5 rounded-2xl">
                    <div className="flex items-center gap-1 mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                      ))}
                    </div>
                    <p className="text-white/70 mb-4 italic">"{testimonial.text}"</p>
                    <div>
                      <p className="text-white font-medium">{testimonial.name}</p>
                      <p className="text-white/40 text-sm">{testimonial.location}</p>
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 bg-black/30">
          <div className="container mx-auto px-4">
            <AnimatedSection animation="fade-up">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Questions fréquentes
                </h2>
                <p className="text-white/60">
                  Tout ce que vous devez savoir sur l'installation d'alarme à Montpellier
                </p>
              </div>
            </AnimatedSection>

            <div className="max-w-3xl mx-auto space-y-4">
              {faqItems.map((item, index) => (
                <AnimatedSection key={index} animation="fade-up" delay={index * 100}>
                  <details className="group p-6 bg-white/[0.02] border border-white/5 rounded-xl">
                    <summary className="flex items-center justify-between cursor-pointer list-none">
                      <h3 className="text-lg font-medium text-white pr-4">{item.question}</h3>
                      <ChevronRight className="w-5 h-5 text-white/40 group-open:rotate-90 transition-transform" />
                    </summary>
                    <p className="mt-4 text-white/60">{item.answer}</p>
                  </details>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>

        {/* Formulaire de devis */}
        <section id="devis" className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto">
              <AnimatedSection animation="fade-up">
                <div className="text-center mb-12">
                  <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                    Demandez votre <span className="text-emerald-400">devis gratuit</span>
                  </h2>
                  <p className="text-white/60">
                    Réponse garantie sous 24h. Installation sous 48-72h.
                  </p>
                </div>
              </AnimatedSection>

              <AnimatedSection animation="fade-up" delay={100}>
                {submitted ? (
                  <div className="p-8 bg-emerald-500/10 border border-emerald-500/30 rounded-2xl text-center">
                    <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-white mb-2">Demande envoyée !</h3>
                    <p className="text-white/60">
                      Nous vous recontactons sous 24h pour établir votre devis personnalisé.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="p-8 bg-white/[0.02] border border-white/10 rounded-2xl space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name" className="text-white mb-2 block">Nom complet *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="bg-white/5 border-white/10 text-white"
                          placeholder="Jean Dupont"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone" className="text-white mb-2 block">Téléphone *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="bg-white/5 border-white/10 text-white"
                          placeholder="06 12 34 56 78"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-white mb-2 block">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="bg-white/5 border-white/10 text-white"
                        placeholder="jean.dupont@email.com"
                      />
                    </div>

                    <div>
                      <Label htmlFor="address" className="text-white mb-2 block">Adresse / Ville *</Label>
                      <Input
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        required
                        className="bg-white/5 border-white/10 text-white"
                        placeholder="34000 Montpellier"
                      />
                    </div>

                    <div>
                      <Label className="text-white mb-2 block">Type de bien</Label>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="propertyType"
                            value="maison"
                            checked={formData.propertyType === 'maison'}
                            onChange={handleChange}
                            className="text-emerald-500"
                          />
                          <span className="text-white/70">Maison</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="propertyType"
                            value="appartement"
                            checked={formData.propertyType === 'appartement'}
                            onChange={handleChange}
                            className="text-emerald-500"
                          />
                          <span className="text-white/70">Appartement</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="propertyType"
                            value="entreprise"
                            checked={formData.propertyType === 'entreprise'}
                            onChange={handleChange}
                            className="text-emerald-500"
                          />
                          <span className="text-white/70">Entreprise</span>
                        </label>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-white mb-2 block">Message (optionnel)</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        className="bg-white/5 border-white/10 text-white min-h-[100px]"
                        placeholder="Décrivez votre projet (nombre de pièces, besoins spécifiques...)"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-6 text-lg"
                    >
                      {loading ? 'Envoi en cours...' : 'Recevoir mon devis gratuit'}
                    </Button>

                    <p className="text-white/40 text-sm text-center">
                      En soumettant ce formulaire, vous acceptez d'être recontacté par notre équipe.
                    </p>
                  </form>
                )}
              </AnimatedSection>
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-16 bg-gradient-to-r from-emerald-500/20 to-blue-500/20">
          <div className="container mx-auto px-4 text-center">
            <AnimatedSection animation="fade-up">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                Besoin d'une installation urgente ?
              </h2>
              <p className="text-white/60 mb-6">
                Appelez-nous directement pour une intervention rapide à Montpellier
              </p>
              <a href="tel:+33467000000" className="inline-flex items-center gap-2 text-2xl font-bold text-emerald-400 hover:text-emerald-300 transition-colors">
                <Phone className="w-6 h-6" />
                04 67 00 00 00
              </a>
            </AnimatedSection>
          </div>
        </section>
      </div>
    </>
  );
};

export default AlarmeMontpellierPage;
